import { useTranslation } from "react-i18next";

import { useRealtimeStore } from "@/realtime/store";

/**
 * Shows the socket only when it is *not* healthy.
 *
 * A permanent green dot is noise that people stop seeing within a day, which
 * makes it useless on the day it turns red. What matters is the opposite
 * signal: the UI may be stale and you cannot tell by looking at it
 * (.docs/04 §5).
 *
 * Three states, two tones. Modernist is mono, so the escalation is carried by
 * the accent rather than by hue: connecting is quiet, everything worse is red.
 * `--color-accent-700` and not the accent itself — the system's own readme
 * warns the accent-to-ground pair is 3:1, which is enough for icons and large
 * type and not for a twelve-pixel label (.docs/08 §7 F6).
 */
const TONES: Record<string, string> = {
  connecting: "tag tag-neutral",
  reconnecting: "tag tag-outline text-brand-700",
  offline: "tag tag-outline text-brand-700",
  closed: "tag tag-outline text-brand-700",
};

export function ConnectionBadge() {
  const { t } = useTranslation();
  const status = useRealtimeStore((state) => state.status);
  const tone = TONES[status];
  if (!tone) return null; // "open" — say nothing

  return (
    <span role="status" className={`${tone} inline-flex items-center gap-1.5`}>
      {/* A square, not a dot: nothing in this system is round. */}
      <span aria-hidden="true" className="h-1.5 w-1.5 bg-current" />
      {t(`connection.${status}`)}
    </span>
  );
}
